<template>
  <fts-modal
    :append-to-body="appendToBody"
    :width="width"
    :full-screen="fullScreen"
    :backdrop="backdrop"
    :escape-event="escapeEvent"
    :persistent="persistent"
    :footer="isFooter"
    :header-class="headerClass"
    :content-class="contentClass"
    :footer-class="footerClass"
    :append-container-class="appendContainerClass"
    class="fts-client-modal"
    @opened="opened"
    @close="beforeClose"
    @focus="focus"
    @blur="blur"
    v-if="showDialog"
  >
    <!-- header -->
    <template #header>
      <!-- header wrapper -->
      <div class="client-modal-header">
        <div class="client-modal-header__icon" v-if="isIcon">
          <slot name="icon" />
        </div>
        <div class="client-modal-header__wrapper">
          <!-- title -->
          <slot class="title">
            <div class="client-modal-header-title">{{title}}</div>
          </slot>
          <!-- description -->
          <slot class="description" v-if="isDescription">
            <div class="client-modal-header-description">{{description}}</div>
          </slot>
        </div>
      </div>
      <!-- actions -->
      <slot name="header-actions">
        <div class="client-modal-header-actions">
          <slot name="actions" />
          <fts-btn text icon @click="beforeClose">
            <font-awesome-icon :icon="['fas', 'times']" />
          </fts-btn>
        </div>
      </slot>
    </template>
    <!-- body -->
    <template #body>
      <slot name="body" />
    </template>
    <!-- footer -->
    <template #footer>
      <slot name="footer">
        <!-- footer left -->
        <div class="client-modal-footer__l">
          <slot name="footerLeft" />
        </div>
        <!-- footer right -->
        <div class="client-modal-footer__r">
          <slot name="footerRight" />
        </div>
      </slot>
    </template>
  </fts-modal>
</template>

<script>
import FTSModal from "./FTSModal";
import FTSBtn from "./FTSBtn";

export default {
  components: {
    "fts-modal": FTSModal,
    "fts-btn": FTSBtn,
  },
  props: {
    appendToBody: Boolean,
    width: String,
    fullScreen: Boolean,
    backdrop: {
      type: Boolean,
      default: true,
    },
    escapeEvent: Boolean,
    persistent: Boolean,
    footer: {
      type: Boolean,
      default: true
    },
    appendHeaderClass: String,
    appendContentClass: String,
    appendFooterClass: String,
    appendContainerClass: String,
    title: String,
    description: String,
  },
  data: () => ({
    showDialog: false,
    beforeCloseHandler: null,
  }),
  methods: {
    show(item) {
      if (item && item.beforeCloseHandler)
        this.beforeCloseHandler = item.beforeCloseHandler;

      this.showDialog = true;
    },
    opened() {
      this.$emit("opened");
    },
    beforeClose() {
      if (this.beforeCloseHandler && typeof this.beforeCloseHandler === 'function') {
        this.beforeCloseHandler();  
      } else {
        this.close();
      }
    },
    close() {
      this.showDialog = false;
      this.$emit("closed");
    },
    focus() {
      this.$emit("focus");
    },
    blur() {
      this.$emit("blur");
    },
  },
  computed: {
    isDescription() {
      return (
        this.description || 
        Boolean(this.$slots.description) || 
        Boolean(this.$scopedSlots.description)
      );
    },
    isIcon() {
      return Boolean(this.$slots.icon) || Boolean(this.$scopedSlots.icon);
    },
    isFooterLeft() {
      return Boolean(this.$slots.footerLeft) || Boolean(this.$scopedSlots.footerLeft);
    },
    isFooterRight() {
      return Boolean(this.$slots.footerRight) || Boolean(this.$scopedSlots.footerRight);
    },
    isFooter() {  
      return (
        this.footer &&
        (
          Boolean(this.$slots.footer) || 
          Boolean(this.$scopedSlots.footer) || 
          this.isFooterLeft ||
          this.isFooterRight
        )
      );
    },
    headerClass() {
      let classes = "client-modal__header";
      if (this.appendHeaderClass)
        classes += ` ${this.appendHeaderClass}`;

      return classes;
    } , 
    contentClass() {
      let classes = "client-modal__body";
      if (this.appendContentClass)
        classes += ` ${this.appendContentClass}`;

      return classes;
    },
    footerClass() {
      let classes = "client-modal__footer";
      if (this.isFooterLeft || this.isFooterRight)
        classes += " row";
      if (this.appendFooterClass)
        classes += ` ${this.appendContentClass}`;
      
      return classes;
    },
  },
}
</script>

<style lang="scss">
@import "./src/assets/scss/vars.scss";

.fts-client-modal {
  .client-modal__header {
    display: flex;
    align-items: start;
    padding: 1rem;
    background: var(--fts-gray-2);
    .client-modal-header {
      min-width: 0;
      flex: 1;
      display: flex;
      align-items: start;
      .client-modal-header__icon {
        margin-right: .575rem;
        font-size: 1.5rem;
        color: $primaryColor;
        line-height: 1.5;
        > * {
          line-height: inherit;
        }
      }
      .client-modal-header__wrapper {
        min-width: 0;
        .client-modal-header-title {
          @include text-overflow-ellipsis;
          font-size: 1.5rem;
          font-weight: 600;
          color: var(--fts-text);
          line-height: 1.5;
        }
        .client-modal-header-description {
          @include theme() {
            color: rgba(theme-get('fts-text'), .65);
          } 
          font-size: .75rem;
          line-height: 1.25;
        }
      }
    }
    .client-modal-header-actions {
      display: flex;
      align-items: center;
      justify-content: flex-end;
      padding-left: .75rem;
    }
  }
  .client-modal__body {
    padding: 1rem;
  }
  .client-modal__footer {
    padding: 1rem;
    border-top: 1px solid var(--fts-gray-2);
    text-align: right;
    &.row {
      display: flex;
    }
    .client-modal-footer__l {
      @include flex-gap($gap: .575rem, $width: auto);
    }
    .client-modal-footer__r {
      @include flex-gap($gap: .575rem, $width: auto);
      justify-content: flex-end;
      flex: 1;
    }
  }
  /* spinner */
  .client-modal-spinner {
    padding: 2rem 0;
  }
}
</style>